export interface TimeSeriesData {
  date: Date;
  value: number;
  original?: boolean; // true for historical data, false for forecasted
}

export interface ForecastResult {
  data: TimeSeriesData[];
  confidence: 'high' | 'medium' | 'low';
  method: string;
  accuracy?: number;
  trend: 'increasing' | 'decreasing' | 'stable';
  seasonality?: 'detected' | 'none';
  recommendations: string[];
}

export interface ForecastConfig {
  periods: number; // Number of periods to forecast (default 6 months)
  method?: 'auto' | 'linear_regression' | 'moving_average' | 'exponential_smoothing';
  confidenceInterval?: boolean;
  minimumDataPoints?: number;
}

export class ForecastingEngine {
  private data: TimeSeriesData[];
  private config: ForecastConfig;

  constructor(data: TimeSeriesData[], config: ForecastConfig = { periods: 6 }) {
    this.data = data.filter(d => !isNaN(d.value) && d.date instanceof Date);
    this.config = { minimumDataPoints: 6, method: 'auto', ...config };
  }

  forecast(): ForecastResult | null {
    if (!this.isDataSuitableForForecasting()) {
      return null;
    }

    // Sort data by date
    this.data.sort((a, b) => a.date.getTime() - b.date.getTime());

    // Determine best forecasting method
    const method = this.config.method === 'auto' ? 
      this.selectBestMethod() : this.config.method!;

    let forecastData: TimeSeriesData[];
    let accuracy: number;

    switch (method) {
      case 'linear_regression':
        ({ forecastData, accuracy } = this.linearRegressionForecast());
        break;
      case 'moving_average':
        ({ forecastData, accuracy } = this.movingAverageForecast());
        break;
      case 'exponential_smoothing':
        ({ forecastData, accuracy } = this.exponentialSmoothingForecast());
        break;
      default:
        ({ forecastData, accuracy } = this.linearRegressionForecast());
    }

    const trend = this.detectTrend();
    const seasonality = this.detectSeasonality();
    const confidence = this.calculateConfidence(accuracy, this.data.length);

    return {
      data: [...this.data.map(d => ({ ...d, original: true })), ...forecastData],
      confidence,
      method,
      accuracy,
      trend,
      seasonality,
      recommendations: this.generateRecommendations(confidence, trend, accuracy)
    };
  }

  private isDataSuitableForForecasting(): boolean {
    if (this.data.length < this.config.minimumDataPoints!) {
      return false;
    }

    // Check for reasonable time intervals
    const intervals = this.calculateIntervals();
    const avgInterval = intervals.reduce((sum, interval) => sum + interval, 0) / intervals.length;
    const maxInterval = Math.max(...intervals);
    
    // Data should have reasonably consistent intervals (max interval shouldn't be > 3x average)
    return maxInterval <= avgInterval * 3;
  }

  private calculateIntervals(): number[] {
    const intervals: number[] = [];
    for (let i = 1; i < this.data.length; i++) {
      const intervalDays = (this.data[i].date.getTime() - this.data[i-1].date.getTime()) / (1000 * 60 * 60 * 24);
      intervals.push(intervalDays);
    }
    return intervals;
  }

  private selectBestMethod(): string {
    const dataLength = this.data.length;
    const trend = this.detectTrend();
    
    // For data with clear trends, use linear regression
    if (trend !== 'stable' && dataLength >= 12) {
      return 'linear_regression';
    }
    
    // For stable data or smaller datasets, use moving average
    if (dataLength < 12) {
      return 'moving_average';
    }
    
    // For larger datasets with potential seasonality, use exponential smoothing
    return 'exponential_smoothing';
  }

  private linearRegressionForecast(): { forecastData: TimeSeriesData[], accuracy: number } {
    // Convert dates to numeric values (days since first date)
    const firstDate = this.data[0].date.getTime();
    const xValues = this.data.map(d => (d.date.getTime() - firstDate) / (1000 * 60 * 60 * 24));
    const yValues = this.data.map(d => d.value);

    // Calculate linear regression coefficients
    const n = xValues.length;
    const sumX = xValues.reduce((sum, x) => sum + x, 0);
    const sumY = yValues.reduce((sum, y) => sum + y, 0);
    const sumXY = xValues.reduce((sum, x, i) => sum + x * yValues[i], 0);
    const sumX2 = xValues.reduce((sum, x) => sum + x * x, 0);

    const slope = (n * sumXY - sumX * sumY) / (n * sumX2 - sumX * sumX);
    const intercept = (sumY - slope * sumX) / n;

    // Calculate R-squared for accuracy
    const meanY = sumY / n;
    const totalSumSquares = yValues.reduce((sum, y) => sum + Math.pow(y - meanY, 2), 0);
    const residualSumSquares = yValues.reduce((sum, y, i) => {
      const predicted = slope * xValues[i] + intercept;
      return sum + Math.pow(y - predicted, 2);
    }, 0);
    const rSquared = 1 - (residualSumSquares / totalSumSquares);

    // Generate forecast data
    const avgInterval = this.calculateAverageInterval();
    const forecastData: TimeSeriesData[] = [];
    
    for (let i = 1; i <= this.config.periods; i++) {
      const lastDate = this.data[this.data.length - 1].date;
      const forecastDate = new Date(lastDate.getTime() + (avgInterval * i * 24 * 60 * 60 * 1000));
      const x = (forecastDate.getTime() - firstDate) / (1000 * 60 * 60 * 24);
      const predictedValue = slope * x + intercept;
      
      forecastData.push({
        date: forecastDate,
        value: Math.max(0, predictedValue), // Ensure non-negative values
        original: false
      });
    }

    return { forecastData, accuracy: Math.max(0, rSquared) };
  }

  private movingAverageForecast(): { forecastData: TimeSeriesData[], accuracy: number } {
    const windowSize = Math.min(6, Math.floor(this.data.length / 3));
    const lastValues = this.data.slice(-windowSize).map(d => d.value);
    const average = lastValues.reduce((sum, val) => sum + val, 0) / lastValues.length;

    // Calculate accuracy using mean absolute percentage error on last few points
    const predictions = this.data.slice(-windowSize).map(() => average);
    const mape = this.calculateMAPE(lastValues, predictions);
    const accuracy = Math.max(0, 1 - mape);

    const avgInterval = this.calculateAverageInterval();
    const forecastData: TimeSeriesData[] = [];
    
    for (let i = 1; i <= this.config.periods; i++) {
      const lastDate = this.data[this.data.length - 1].date;
      const forecastDate = new Date(lastDate.getTime() + (avgInterval * i * 24 * 60 * 60 * 1000));
      
      forecastData.push({
        date: forecastDate,
        value: Math.max(0, average),
        original: false
      });
    }

    return { forecastData, accuracy };
  }

  private exponentialSmoothingForecast(): { forecastData: TimeSeriesData[], accuracy: number } {
    const alpha = 0.3; // Smoothing parameter
    const values = this.data.map(d => d.value);
    
    // Calculate exponentially smoothed values
    let smoothed = values[0];
    const smoothedValues = [smoothed];
    
    for (let i = 1; i < values.length; i++) {
      smoothed = alpha * values[i] + (1 - alpha) * smoothed;
      smoothedValues.push(smoothed);
    }

    // Calculate accuracy
    const mape = this.calculateMAPE(values.slice(1), smoothedValues.slice(1));
    const accuracy = Math.max(0, 1 - mape);

    const lastSmoothed = smoothedValues[smoothedValues.length - 1];
    const avgInterval = this.calculateAverageInterval();
    const forecastData: TimeSeriesData[] = [];
    
    for (let i = 1; i <= this.config.periods; i++) {
      const lastDate = this.data[this.data.length - 1].date;
      const forecastDate = new Date(lastDate.getTime() + (avgInterval * i * 24 * 60 * 60 * 1000));
      
      forecastData.push({
        date: forecastDate,
        value: Math.max(0, lastSmoothed),
        original: false
      });
    }

    return { forecastData, accuracy };
  }

  private calculateAverageInterval(): number {
    const intervals = this.calculateIntervals();
    return intervals.reduce((sum, interval) => sum + interval, 0) / intervals.length;
  }

  private calculateMAPE(actual: number[], predicted: number[]): number {
    if (actual.length !== predicted.length) return 1;
    
    let sumPercentageError = 0;
    let validPoints = 0;
    
    for (let i = 0; i < actual.length; i++) {
      if (actual[i] !== 0) {
        sumPercentageError += Math.abs((actual[i] - predicted[i]) / actual[i]);
        validPoints++;
      }
    }
    
    return validPoints > 0 ? sumPercentageError / validPoints : 1;
  }

  private detectTrend(): 'increasing' | 'decreasing' | 'stable' {
    if (this.data.length < 3) return 'stable';
    
    const values = this.data.map(d => d.value);
    const firstHalf = values.slice(0, Math.floor(values.length / 2));
    const secondHalf = values.slice(Math.floor(values.length / 2));
    
    const firstAvg = firstHalf.reduce((sum, val) => sum + val, 0) / firstHalf.length;
    const secondAvg = secondHalf.reduce((sum, val) => sum + val, 0) / secondHalf.length;
    
    const changePercentage = Math.abs((secondAvg - firstAvg) / firstAvg);
    
    if (changePercentage < 0.1) return 'stable';
    return secondAvg > firstAvg ? 'increasing' : 'decreasing';
  }

  private detectSeasonality(): 'detected' | 'none' {
    // Simple seasonality detection - look for repeating patterns
    if (this.data.length < 12) return 'none';
    
    const values = this.data.map(d => d.value);
    const autocorrelation = this.calculateAutocorrelation(values, 12);
    
    // If autocorrelation at lag 12 is significant, there might be yearly seasonality
    return Math.abs(autocorrelation) > 0.3 ? 'detected' : 'none';
  }

  private calculateAutocorrelation(values: number[], lag: number): number {
    if (values.length <= lag) return 0;
    
    const n = values.length - lag;
    const mean = values.reduce((sum, val) => sum + val, 0) / values.length;
    
    let numerator = 0;
    let denominator = 0;
    
    for (let i = 0; i < n; i++) {
      numerator += (values[i] - mean) * (values[i + lag] - mean);
    }
    
    for (let i = 0; i < values.length; i++) {
      denominator += Math.pow(values[i] - mean, 2);
    }
    
    return denominator !== 0 ? numerator / denominator : 0;
  }

  private calculateConfidence(accuracy: number, dataPoints: number): 'high' | 'medium' | 'low' {
    if (accuracy > 0.8 && dataPoints >= 24) return 'high';
    if (accuracy > 0.6 && dataPoints >= 12) return 'medium';
    return 'low';
  }

  private generateRecommendations(confidence: string, trend: string, accuracy: number): string[] {
    const recommendations: string[] = [];
    
    if (confidence === 'low') {
      recommendations.push('Consider collecting more historical data for better forecast accuracy');
    }
    
    if (accuracy < 0.5) {
      recommendations.push('Forecast has limited reliability - use as directional guidance only');
    }
    
    if (trend === 'increasing') {
      recommendations.push('Upward trend detected - consider scaling resources accordingly');
    } else if (trend === 'decreasing') {
      recommendations.push('Downward trend detected - investigate underlying causes');
    }
    
    recommendations.push('Monitor actual values against forecast to improve future predictions');
    
    return recommendations;
  }

  static isTimeSeriesData(data: any[], dateColumn: string, valueColumn: string): boolean {
    if (data.length < 6) return false;
    
    // Check if we have valid dates and numeric values
    const validPoints = data.filter(row => {
      const date = new Date(row[dateColumn]);
      const value = Number(row[valueColumn]);
      return !isNaN(date.getTime()) && !isNaN(value);
    });
    
    return validPoints.length >= 6 && validPoints.length / data.length > 0.8;
  }

  static convertToTimeSeriesData(data: any[], dateColumn: string, valueColumn: string): TimeSeriesData[] {
    return data
      .map(row => ({
        date: new Date(row[dateColumn]),
        value: Number(row[valueColumn]),
        original: true
      }))
      .filter(item => !isNaN(item.date.getTime()) && !isNaN(item.value))
      .sort((a, b) => a.date.getTime() - b.date.getTime());
  }
}